/**
 * <collection-carousel-component>
 * Lead-carousel viewport for the Collection Carousel section.
 * Trackpad and touch scrolling are handled natively by the overflow container;
 * this class adds pointer drag and keyboard arrow-key navigation.
 */
class CollectionCarouselComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cc-track]');
    if (!this.track) return;

    this.track.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('keydown', this.#onKeyDown);
    // Trap accidental link activations that happen during a drag.
    this.track.addEventListener('click', this.#onClickCapture, true);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.track?.removeEventListener('click', this.#onClickCapture, true);
  }

  #dragState = null;

  #onPointerDown = (event) => {
    if (event.pointerType === 'touch') return; // native touch scroll wins
    if (event.button !== 0) return;
    if (event.target.closest('a')) return; // don't hijack link/button clicks

    const startX = event.clientX;
    const startScroll = this.track.scrollLeft;

    this.#dragState = { startX, startScroll, moved: false, pointerId: event.pointerId };
    this.track.setPointerCapture?.(event.pointerId);
    this.track.setAttribute('data-cc-dragging', '');

    this.track.addEventListener('pointermove', this.#onPointerMove);
    this.track.addEventListener('pointerup', this.#onPointerUp);
    this.track.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (!this.#dragState) return;
    const dx = event.clientX - this.#dragState.startX;
    if (Math.abs(dx) > 10) this.#dragState.moved = true;
    this.track.scrollLeft = this.#dragState.startScroll - dx;
  };

  #onPointerUp = (event) => {
    const state = this.#dragState;
    if (!state) return;
    this.track.releasePointerCapture?.(state.pointerId);
    this.track.removeAttribute('data-cc-dragging');
    this.track.removeEventListener('pointermove', this.#onPointerMove);
    this.track.removeEventListener('pointerup', this.#onPointerUp);
    this.track.removeEventListener('pointercancel', this.#onPointerUp);

    if (state.moved) this.#snapToNearest();
    // Reset on next microtask so the click handler can read `moved` first.
    queueMicrotask(() => {
      this.#dragState = null;
    });
  };

  #onClickCapture = (event) => {
    if (this.#dragState?.moved) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  #onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }
    event.preventDefault();

    const slides = this.#getSlides();
    if (!slides.length) return;

    const current = this.#currentIndex(slides);
    let target = current;
    // In RTL the next slide sits to the left.
    const step = this.#isRtl() ? -1 : 1;
    if (event.key === 'ArrowLeft') target = Math.min(slides.length - 1, Math.max(0, current - step));
    if (event.key === 'ArrowRight') target = Math.min(slides.length - 1, Math.max(0, current + step));
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = slides.length - 1;

    this.#scrollToSlide(slides[target]);
  };

  #isRtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  #getSlides() {
    return Array.from(this.track.children);
  }

  #maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  #currentIndex(slides) {
    const maxScroll = this.#maxScroll();
    // Last slide snaps to the trailing edge (no end padding), so treat anything
    // near max scroll as the last card — avoids snap-back to the previous one.
    const endThreshold = Math.max(120, this.track.clientWidth * 0.08);
    if (maxScroll > 0 && Math.abs(this.track.scrollLeft) >= maxScroll - endThreshold) {
      return slides.length - 1;
    }

    const trackRect = this.track.getBoundingClientRect();
    // Slides start at the right edge in RTL (scrollLeft runs from 0 to -maxScroll).
    const start = this.#isRtl() ? 'right' : 'left';
    const end = this.#isRtl() ? 'left' : 'right';
    let best = 0;
    let bestDelta = Infinity;
    slides.forEach((slide, i) => {
      const slideRect = slide.getBoundingClientRect();
      const isLast = i === slides.length - 1;
      const delta = isLast
        ? Math.abs(slideRect[end] - trackRect[end])
        : Math.abs(slideRect[start] - trackRect[start]);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = i;
      }
    });
    return best;
  }

  #scrollToSlide(slide) {
    const slides = this.#getSlides();
    if (slide === slides[slides.length - 1]) {
      this.track.scrollTo({
        left: this.#isRtl() ? -this.#maxScroll() : this.#maxScroll(),
        behavior: 'smooth',
      });
      return;
    }

    const edge = this.#isRtl() ? 'right' : 'left';
    const trackEdge = this.track.getBoundingClientRect()[edge];
    const slideEdge = slide.getBoundingClientRect()[edge];
    const targetLeft = this.track.scrollLeft + (slideEdge - trackEdge);
    this.track.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }

  #snapToNearest() {
    const slides = this.#getSlides();
    if (!slides.length) return;
    this.#scrollToSlide(slides[this.#currentIndex(slides)]);
  }
}

if (!customElements.get('collection-carousel-component')) {
  customElements.define('collection-carousel-component', CollectionCarouselComponent);
}